import { Injectable } from '@angular/core';

/**
 * The filling helper's logic, kept out of signing.component.ts.
 *
 * Everything here is a pure function over field records and the live `formFieldValues` map.
 * No DOM, no Angular state. The component owns scroll/highlight/focus; this owns "which field,
 * in what order, and is it filled".
 *
 * The one rule that matters: `isUnfilled` is the ONLY definition of emptiness on the signing
 * page. The counter, Next, and ApproveSign's gate all call it. If the counter ever computed its
 * own rules, it could read "0 left" while Approve still refused.
 */

/** The types a signer can be walked through. `sign-area` (QES) is deliberately absent — the QES
 *  panel comes after Approve, so it is never a traversal stop and never counted. */
export const TRAVERSABLE_TYPES = ['text-input', 'checkbox', 'radio', 'bio-sign', 'initials'];

/** The non-QES signature boxes. These share one branch everywhere they are treated specially
 *  (traversability, focus) — `initials` has no separate E2E job precisely because it has no
 *  separate branch. Splitting them means the initials path needs its own coverage. */
export const SIGNATURE_STOP_TYPES = ['bio-sign', 'initials'];

export interface FillingStop {
  /** Stable identity: "field:1234" or "radio:agree_terms". */
  key: string;
  page: number;
  /** The field whose overlay element is scrolled to, highlighted and focused. */
  anchorFieldId: number;
  /** One field, or every option of a radio group. */
  fields: any[];
  type: string;
  /** True when any field in the stop is required — matches how the gate treats a radio group. */
  required: boolean;
}

@Injectable()
export class FillingHelperService {

  /**
   * The key a radio field's selection is stored under in `formFieldValues`.
   *
   * The `radio-${id}` fallback is NOT optional. Every place in signing.component.ts that touches
   * radio state uses it — the initial load, the render, the change listener and the sync. A radio
   * with no `variable` would otherwise resolve against `formFieldValues[""]`, could never read as
   * filled, and — because the Approve gate shares this predicate — would block the signer on a
   * page where they can see the radio is selected.
   */
  radioGroupKey(field: any): string {
    return field.variable || `radio-${field.id}`;
  }

  /**
   * The current value of a field, as a string, from whichever source actually holds it.
   *
   * For text/checkbox/radio the live value is in `formFieldValues`; `field.value` is only brought
   * into step by syncHtmlFormFieldsToSubmission at Approve time. These rules mirror that sync
   * exactly — and the sync is written in terms of this method, so there is one rule set rather
   * than two that can drift.
   *
   * Returns undefined when the map holds nothing for the field; callers fall back to
   * `field.value` / `field.initialValue`, as the original gate did.
   */
  resolveValue(field: any, formFieldValues: { [k: string]: any }, ownFields: any[]): string | undefined {
    if (field.type === 'text-input') {
      const v = formFieldValues[field.id];
      return v === undefined || v === null ? undefined : String(v);
    }

    if (field.type === 'checkbox') {
      const v = formFieldValues[field.id];
      if (v === undefined) { return undefined; }
      return v === true || v === 'true' ? 'true' : 'false';
    }

    if (field.type === 'radio') {
      const selected = formFieldValues[this.radioGroupKey(field)];
      return selected !== undefined && selected === String(field.id) ? 'true' : 'false';
    }

    // bio-sign / initials — the signature paths write field.value directly.
    return field.value;
  }

  /** Every option of the radio group this field belongs to, within the signer's own fields. */
  radioGroupFields(field: any, ownFields: any[]): any[] {
    const key = this.radioGroupKey(field);
    return ownFields.filter((f: any) => f.type === 'radio' && this.radioGroupKey(f) === key);
  }

  /**
   * THE predicate. Lifted from ApproveSign's original loop, reading through resolveValue.
   *
   * The radio clause is scoped to the signer's own fields. The original scanned every recipient's
   * fields, which let another recipient's answer satisfy this signer's group — see
   * docs/design/signing-component.md.
   */
  isUnfilled(field: any, formFieldValues: { [k: string]: any }, ownFields: any[]): boolean {
    const resolved = this.resolveValue(field, formFieldValues, ownFields);

    switch (field.type) {
      case 'text-input': {
        const v = resolved !== undefined ? resolved : (field.value || field.initialValue);
        return !v || v.length === 0;
      }
      case 'checkbox':
        return resolved !== 'true';
      case 'radio':
        return !this.radioGroupFields(field, ownFields)
          .some((f: any) => this.resolveValue(f, formFieldValues, ownFields) === 'true');
      default:
        // bio-sign / initials
        return field.value !== 'true';
    }
  }

  /**
   * Is this field one the helper walks?
   *
   * `enabled` is a STRING ("true"/"false"). Comparing it for truthiness — as three pre-existing
   * lines in the overlay builders do — is always true, which would walk the signer into fields the
   * preparer meant to be inert.
   */
  isTraversable(field: any, recipientId: any): boolean {
    return Number(field.recipientId) === Number(recipientId)
      && String(field.enabled) === 'true'
      && TRAVERSABLE_TYPES.indexOf(field.type) !== -1;
  }

  /** Traversable and mandatory — exactly the set the Approve gate checks. */
  isCountable(field: any, recipientId: any): boolean {
    return this.isTraversable(field, recipientId) && String(field.required) === 'true';
  }

  /**
   * Reading order, derived from geometry.
   *
   * `orderno` is NOT reading order — both writers set it to the element's index in the form
   * designer, so a field at the bottom of page 3 can precede one at the top of page 1. It survives
   * here only as a stable tiebreaker.
   *
   * This runs on the stored PDF record, never on screen coordinates, so it is correct before any
   * page has rendered. PDF coordinates have a bottom-left origin, so the top edge is `y + height`
   * and sorts DESCENDING for top-to-bottom.
   */
  readingOrderCompare = (a: any, b: any): number => {
    const page = Number(a.page) - Number(b.page);
    if (page !== 0) { return page; }

    const top = (Number(b.y) + Number(b.height)) - (Number(a.y) + Number(a.height));
    if (top !== 0) { return top; }

    const x = Number(a.x) - Number(b.x);
    if (x !== 0) { return x; }

    const order = Number(a.orderno || 0) - Number(b.orderno || 0);
    if (order !== 0) { return order; }

    return Number(a.id) - Number(b.id);
  };

  /**
   * The traversal stops, in reading order.
   *
   * Radios sharing a group key fold into ONE stop, anchored at the group's first option in
   * reading order — so Next visits the group once, not once per option, and the counter counts it
   * once.
   */
  buildStops(ownFields: any[], recipientId: any): FillingStop[] {
    const ordered = ownFields
      .filter((f: any) => this.isTraversable(f, recipientId))
      .slice()
      .sort(this.readingOrderCompare);

    const stops: FillingStop[] = [];
    const radioStopByKey: { [k: string]: FillingStop } = {};

    ordered.forEach((field: any) => {
      if (field.type === 'radio') {
        const key = this.radioGroupKey(field);
        const existing = radioStopByKey[key];
        if (existing) {
          existing.fields.push(field);
          existing.required = existing.required || String(field.required) === 'true';
          return;
        }
        const stop: FillingStop = {
          key: `radio:${key}`,
          page: Number(field.page),
          anchorFieldId: Number(field.id),
          fields: [field],
          type: 'radio',
          required: String(field.required) === 'true'
        };
        radioStopByKey[key] = stop;
        stops.push(stop);
        return;
      }

      stops.push({
        key: `field:${field.id}`,
        page: Number(field.page),
        anchorFieldId: Number(field.id),
        fields: [field],
        type: field.type,
        required: String(field.required) === 'true'
      });
    });

    return stops;
  }

  /** A stop is unfilled when its field is — for a radio group, the group test, which is the same
   *  for every option, so it does not matter which one is asked. */
  isStopUnfilled(stop: FillingStop, formFieldValues: { [k: string]: any }, ownFields: any[]): boolean {
    return this.isUnfilled(stop.fields[0], formFieldValues, ownFields);
  }

  /**
   * How many mandatory stops are still outstanding.
   *
   * This is zero exactly when the Approve gate passes: every countable field belongs to one stop,
   * a stop is required iff any of its fields is, and a stop is unfilled iff its fields are. The
   * raw counts differ for a radio group — the gate collects one fillAlertShapes entry per
   * unsatisfied option while this counts the group once — which is the requested behaviour and
   * does not affect the equivalence.
   */
  remainingMandatory(stops: FillingStop[], formFieldValues: { [k: string]: any }, ownFields: any[]): number {
    return stops.filter((s) => s.required && this.isStopUnfilled(s, formFieldValues, ownFields)).length;
  }

  /** The set Next/Previous walk: unfilled stops while any remain, otherwise all of them (review). */
  activeStops(stops: FillingStop[], formFieldValues: { [k: string]: any }, ownFields: any[]): FillingStop[] {
    const unfilled = stops.filter((s) => this.isStopUnfilled(s, formFieldValues, ownFields));
    return unfilled.length > 0 ? unfilled : stops;
  }

  /**
   * The next stop after `current`, by reading-order position rather than by index.
   *
   * Positional matters: the stop the signer is standing on has usually just been filled, so it is
   * no longer in `active` and an index lookup would have nothing to count from.
   */
  nextStop(stops: FillingStop[], current: FillingStop | null,
           formFieldValues: { [k: string]: any }, ownFields: any[]): FillingStop | null {
    const active = this.activeStops(stops, formFieldValues, ownFields);
    if (active.length === 0) { return null; }
    if (!current) { return active[0]; }

    const position = stops.indexOf(current);
    const after = active.find((s) => stops.indexOf(s) > position);
    return after || active[0];
  }

  /** Previous, same rule backwards, wrapping to the last. */
  previousStop(stops: FillingStop[], current: FillingStop | null,
               formFieldValues: { [k: string]: any }, ownFields: any[]): FillingStop | null {
    const active = this.activeStops(stops, formFieldValues, ownFields);
    if (active.length === 0) { return null; }
    if (!current) { return active[active.length - 1]; }

    const position = stops.indexOf(current);
    const before = active.filter((s) => stops.indexOf(s) < position);
    return before.length ? before[before.length - 1] : active[active.length - 1];
  }

  /** The first outstanding MANDATORY stop in reading order — where a blocked Approve lands. */
  firstOutstandingStop(stops: FillingStop[], formFieldValues: { [k: string]: any },
                       ownFields: any[]): FillingStop | null {
    return stops.find((s) => s.required && this.isStopUnfilled(s, formFieldValues, ownFields)) || null;
  }
}
